import React, { useState } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../../lib/supabase";

const inputStyle = { width:"100%", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"9px 12px", color:"#151515", fontSize:13, outline:"none", boxSizing:"border-box", fontFamily:"inherit" };
const labelStyle = { fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:6, display:"block" };
const fieldStyle = { marginBottom:16 };

// Uniform $/% inputs — differ only in label/placeholder/max/hint, so they're
// rendered from this config instead of near-identical JSX blocks.
const MONEY_FIELDS = [
  { key: "amount", label: "Credit Amount ($)", placeholder: "10000.00", hint: "Total credit pool. Consumed once over the credit's whole effective window — it does not reset monthly." },
  { key: "percentageRate", label: "Percentage Rate (%)", placeholder: "50", max: 100, hint: "Ratio of conversions considered credited. Must be greater than 0." },
  { key: "minThresholdSpend", label: "Minimum Threshold Spend ($)", placeholder: "1000.00", hint: "Credit only applies to spend above this floor." },
];

function toDateInputValue(iso) {
  if (!iso) return "";
  return String(iso).slice(0, 10);
}

// CreditFormModal — add/edit form for a single advertiser_credits row.
// Writes go directly to Supabase (RLS grants internal-support full write
// access, see supabase-migrations/046_advertiser_credits.sql). A credit is
// always both an `amount` (the total dollar pool) and a `percentage_rate`
// (the ratio of conversions considered credited, driving the running-ratio
// allocation in conversions-processor's mark_billable.py) — both required.
export function CreditFormModal({ advertiserId, credit, onClose, onSaved }) {
  const isEdit = !!credit;
  const [fields, setFields] = useState({
    amount: credit?.amount != null ? String(credit.amount) : "",
    percentageRate: credit?.percentage_rate != null ? String(parseFloat((credit.percentage_rate * 100).toFixed(4))) : "",
    minThresholdSpend: credit?.min_threshold_spend != null ? String(credit.min_threshold_spend) : "0",
  });
  const setField = (key) => (e) => setFields((prev) => ({ ...prev, [key]: e.target.value }));
  const [effectiveStartDate, setEffectiveStartDate] = useState(toDateInputValue(credit?.effective_start_date) || new Date().toISOString().slice(0, 10));
  const [effectiveEndDate, setEffectiveEndDate] = useState(toDateInputValue(credit?.effective_end_date));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!supabase) return;
    setError(null);

    const parsedAmount = fields.amount.trim() ? parseFloat(fields.amount) : null;
    const parsedRate = fields.percentageRate.trim() ? parseFloat(fields.percentageRate) / 100 : null;
    const parsedThreshold = fields.minThresholdSpend.trim() ? parseFloat(fields.minThresholdSpend) : 0;

    if (parsedAmount == null || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError("Credit amount is required and must be greater than 0.");
      return;
    }
    if (parsedRate == null || Number.isNaN(parsedRate) || parsedRate <= 0) {
      setError("Percentage rate is required and must be greater than 0%.");
      return;
    }
    if (parsedRate > 1) {
      setError("Percentage rate must be 100% or less.");
      return;
    }
    if (Number.isNaN(parsedThreshold) || parsedThreshold < 0) {
      setError("Minimum threshold spend must be 0 or a positive number.");
      return;
    }
    if (!effectiveStartDate) {
      setError("Effective start date is required.");
      return;
    }

    setSaving(true);
    const payload = {
      advertiser_id: advertiserId,
      amount: parsedAmount,
      percentage_rate: parsedRate,
      min_threshold_spend: parsedThreshold,
      effective_start_date: effectiveStartDate,
      effective_end_date: effectiveEndDate || null,
    };

    try {
      const { data: savedRow, error: saveError } = isEdit
        ? await supabase.from("advertiser_credits").update(payload).eq("id", credit.id).select().single()
        : await supabase.from("advertiser_credits").insert(payload).select().single();
      if (saveError) {
        setError(saveError.message || "Failed to save credit.");
        return;
      }
      onSaved?.(savedRow);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to save credit.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:20, width:"100%", maxWidth:420, maxHeight:"90vh", overflowY:"auto", position:"relative", boxShadow:"0 20px 60px rgba(0,0,0,0.15)" }}>
        <div style={{ padding:"24px 24px 16px", borderBottom:"1px solid #F0F0F0" }}>
          <button onClick={onClose} style={{ position:"absolute", top:16, right:20, background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          <h3 style={{ fontSize:16, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>
            {isEdit ? "Edit Credit" : "Issue Credit"}
          </h3>
        </div>

        <div style={{ padding:"20px 24px" }}>
          {MONEY_FIELDS.map(({ key, label, placeholder, max, hint }) => (
            <div key={key} style={fieldStyle}>
              <label style={labelStyle}>{label}</label>
              <input style={inputStyle} type="number" step="0.01" min="0" max={max} placeholder={placeholder} value={fields[key]} onChange={setField(key)} />
              {hint && <div style={{ fontSize:11, color:"#7b7b7b", marginTop:4 }}>{hint}</div>}
            </div>
          ))}

          <div style={{ display:"flex", gap:12, marginBottom:16 }}>
            <div style={{ flex:1 }}>
              <label style={labelStyle}>Effective Start</label>
              <input style={inputStyle} type="date" value={effectiveStartDate} onChange={e => setEffectiveStartDate(e.target.value)} />
            </div>
            <div style={{ flex:1 }}>
              <label style={labelStyle}>Effective End (optional)</label>
              <input style={inputStyle} type="date" value={effectiveEndDate} onChange={e => setEffectiveEndDate(e.target.value)} />
            </div>
          </div>

          {error && (
            <div style={{ padding:"10px 12px", marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>{error}</div>
          )}

          <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
            <button onClick={onClose} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{ padding:"9px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : isEdit ? "Update" : "Issue Credit"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
